/**
 * A value with a second field shown on hover (`cis_field(detail=...)`, docs/object-contract.md, "Laying out a
 * record"): a line's VAT amount with its VAT rate. The value is a button styled as text, so the keyboard reaches it
 * and focus opens the tooltip as hover does; the detail is also in the button's accessible name, so a screen reader
 * reads "£24.00, VAT rate: 20%" whether or not the tooltip is open. Never a `title=` attribute, which a keyboard or
 * a screen reader may never reach. A phone has no hover, so `inline` writes the detail after the value instead.
 */
import type { ReactNode } from "react";
import type { PresentedDetail } from "@shared-core/domain/object-ui/record";
import { Tooltip, TooltipContent, TooltipTrigger } from "../primitives/tooltip";

/** "VAT rate: 20%" (provisional wording). */
export function detailText(detail: PresentedDetail): string {
  return `${detail.label}: ${detail.value}`;
}

export function DetailValue({
  value,
  detail,
  inline = false,
}: {
  value: ReactNode;
  detail: PresentedDetail;
  /** Write the detail after the value (a phone, which has no hover). */
  inline?: boolean;
}) {
  const text = detailText(detail);
  if (inline)
    return (
      <span>
        {value} <span className="text-xs text-muted-foreground">({text})</span>
      </span>
    );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          // A click in a clickable row is not an open of the row: the value only shows its detail.
          onClick={(e) => e.stopPropagation()}
          // [font:inherit]: theme.css gives every button text-base and medium weight; the value reads like its
          // neighbours, marked only by the dotted underline.
          className="cursor-help rounded-sm text-left [font:inherit] underline decoration-dotted decoration-muted-foreground/60 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {value}
          <span className="sr-only">, {text}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}
